import React, { forwardRef } from 'react';
import { ShieldCheck, Heart, Pill, FileText, Calendar, Users, AlertTriangle, User } from 'lucide-react';

// Printable Health Report Component
const PrintableHealthReport = forwardRef(({ patientData, language = 'en' }, ref) => {
  const t = (en, hi) => language === 'hi' ? hi : en;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const currentDate = new Date().toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div ref={ref} className="print-report bg-white p-8 max-w-4xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div className="border-b-4 border-[#0D2340] pb-4 mb-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-10 w-10 text-[#0D2340]" />
            <div>
              <h1 className="text-2xl font-bold text-[#0D2340]">EmergencyCard Health</h1>
              <p className="text-sm text-slate-500">{t('Your Digital Health Vault', 'आपका डिजिटल स्वास्थ्य वॉल्ट')}</p>
            </div>
          </div>
          <div className="text-right text-sm text-slate-500">
            <p>{t('Generated on', 'तारीख')}: {currentDate}</p>
            <p className="flex items-center justify-end gap-1 mt-1">
              <ShieldCheck className="h-4 w-4 text-green-600" />
              {t('Blockchain Verified', 'ब्लॉकचेन सत्यापित')}
            </p>
          </div>
        </div>
      </div>

      {/* Patient Info */}
      <div className="bg-[#0D2340] text-white p-4 rounded-lg mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
            <User className="h-8 w-8" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{patientData.patient?.name || 'Patient Name'}</h2>
            <p className="opacity-80">{patientData.patient?.phone}</p>
            {patientData.patient?.email && <p className="opacity-80">{patientData.patient?.email}</p>}
          </div>
        </div>
      </div>

      {/* Allergies Warning - if from intake */}
      {patientData.intake?.allergies?.types?.length > 0 && 
       !patientData.intake.allergies.types.includes('none') && (
        <div className="bg-red-50 border-2 border-red-400 p-4 rounded-lg mb-6">
          <div className="flex items-center gap-2 text-red-700 font-bold mb-2">
            <AlertTriangle className="h-5 w-5" />
            {t('ALLERGIES', 'एलर्जी')}
          </div>
          <div className="flex flex-wrap gap-2">
            {patientData.intake.allergies.types.map((allergy, i) => (
              <span key={i} className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                {allergy}
              </span>
            ))}
          </div>
          {patientData.intake.allergies.details && (
            <p className="mt-2 text-red-600 text-sm">{patientData.intake.allergies.details}</p>
          )}
        </div>
      )}

      {/* Medical History Section */}
      {patientData.health_data?.medical_history?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 border-b pb-2">
            <Heart className="h-5 w-5 text-red-500" />
            <h3 className="text-lg font-bold text-[#0D2340]">{t('Medical History', 'चिकित्सा इतिहास')}</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left p-2">{t('Condition', 'बीमारी')}</th>
                <th className="text-left p-2">{t('Since', 'कब से')}</th>
                <th className="text-left p-2">{t('Doctor', 'डॉक्टर')}</th>
                <th className="text-left p-2">{t('Status', 'स्थिति')}</th>
              </tr>
            </thead>
            <tbody>
              {patientData.health_data.medical_history.map((item, i) => (
                <tr key={i} className="border-b">
                  <td className="p-2 font-medium">{item.condition}</td>
                  <td className="p-2">{formatDate(item.diagnosed_date)}</td>
                  <td className="p-2">{item.doctor_name || '-'}</td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      item.status === 'ongoing' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {item.status === 'ongoing' ? t('Ongoing', 'जारी') : t('Resolved', 'ठीक')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Current Prescriptions Section */}
      {patientData.health_data?.prescriptions?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 border-b pb-2">
            <Pill className="h-5 w-5 text-blue-500" />
            <h3 className="text-lg font-bold text-[#0D2340]">{t('Current Prescriptions', 'वर्तमान दवाइयां')}</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left p-2">{t('Medicine', 'दवाई')}</th>
                <th className="text-left p-2">{t('Dosage', 'खुराक')}</th>
                <th className="text-left p-2">{t('Frequency', 'कितनी बार')}</th>
                <th className="text-left p-2">{t('Doctor', 'डॉक्टर')}</th>
              </tr>
            </thead>
            <tbody>
              {patientData.health_data.prescriptions.map((item, i) => (
                <tr key={i} className="border-b">
                  <td className="p-2 font-medium">{item.medication_name}</td>
                  <td className="p-2">{item.dosage}</td>
                  <td className="p-2">{item.frequency}</td>
                  <td className="p-2">{item.doctor_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Test Reports Section */}
      {patientData.health_data?.reports?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 border-b pb-2">
            <FileText className="h-5 w-5 text-green-500" />
            <h3 className="text-lg font-bold text-[#0D2340]">{t('Test Reports', 'जांच रिपोर्ट')}</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left p-2">{t('Report', 'रिपोर्ट')}</th>
                <th className="text-left p-2">{t('Type', 'प्रकार')}</th>
                <th className="text-left p-2">{t('Date', 'तारीख')}</th>
                <th className="text-left p-2">{t('Lab', 'लैब')}</th>
                <th className="text-left p-2">{t('Findings', 'परिणाम')}</th>
              </tr>
            </thead>
            <tbody>
              {patientData.health_data.reports.map((item, i) => (
                <tr key={i} className="border-b">
                  <td className="p-2 font-medium">{item.report_name}</td>
                  <td className="p-2">{item.report_type}</td>
                  <td className="p-2">{formatDate(item.report_date)}</td>
                  <td className="p-2">{item.lab_name || '-'}</td>
                  <td className="p-2 text-xs">{item.findings || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent Vitals Section */}
      {patientData.health_data?.diary?.filter(d => d.entry_type === 'vitals').length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 border-b pb-2">
            <Calendar className="h-5 w-5 text-purple-500" />
            <h3 className="text-lg font-bold text-[#0D2340]">{t('Recent Vitals', 'हाल की जांच')}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {patientData.health_data.diary
              .filter(d => d.entry_type === 'vitals')
              .slice(0, 1)
              .map((vital, i) => (
                <React.Fragment key={i}>
                  {vital.data.blood_pressure && (
                    <div className="bg-red-50 p-3 rounded-lg text-center">
                      <p className="text-xs text-slate-500">{t('Blood Pressure', 'रक्तचाप')}</p>
                      <p className="text-xl font-bold text-red-600">{vital.data.blood_pressure}</p>
                      <p className="text-xs text-slate-400">{formatDate(vital.entry_date)}</p>
                    </div>
                  )}
                  {vital.data.blood_sugar && (
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                      <p className="text-xs text-slate-500">{t('Blood Sugar', 'रक्त शर्करा')}</p>
                      <p className="text-xl font-bold text-blue-600">{vital.data.blood_sugar} mg/dL</p>
                      <p className="text-xs text-slate-400">{formatDate(vital.entry_date)}</p>
                    </div>
                  )}
                  {vital.data.weight && (
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <p className="text-xs text-slate-500">{t('Weight', 'वजन')}</p>
                      <p className="text-xl font-bold text-green-600">{vital.data.weight} kg</p>
                      <p className="text-xs text-slate-400">{formatDate(vital.entry_date)}</p>
                    </div>
                  )}
                  {vital.data.temperature && (
                    <div className="bg-orange-50 p-3 rounded-lg text-center">
                      <p className="text-xs text-slate-500">{t('Temperature', 'तापमान')}</p>
                      <p className="text-xl font-bold text-orange-600">{vital.data.temperature}°F</p>
                      <p className="text-xs text-slate-400">{formatDate(vital.entry_date)}</p>
                    </div>
                  )}
                </React.Fragment>
              ))}
          </div>
        </div>
      )}

      {/* Referrals Section */}
      {patientData.health_data?.referrals?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 border-b pb-2">
            <Users className="h-5 w-5 text-orange-500" />
            <h3 className="text-lg font-bold text-[#0D2340]">{t('Referrals', 'रेफरल')}</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left p-2">{t('From', 'से')}</th>
                <th className="text-left p-2">{t('To', 'को')}</th>
                <th className="text-left p-2">{t('Speciality', 'विशेषज्ञता')}</th>
                <th className="text-left p-2">{t('Reason', 'कारण')}</th>
              </tr>
            </thead>
            <tbody>
              {patientData.health_data.referrals.map((item, i) => (
                <tr key={i} className="border-b">
                  <td className="p-2">{item.referring_doctor}</td>
                  <td className="p-2 font-medium">{item.referred_to_doctor}</td>
                  <td className="p-2">
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                      {item.speciality}
                    </span>
                  </td>
                  <td className="p-2 text-xs">{item.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Family History from Intake */}
      {patientData.intake?.family_history && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 border-b pb-2">
            <Users className="h-5 w-5 text-indigo-500" />
            <h3 className="text-lg font-bold text-[#0D2340]">{t('Family History', 'पारिवारिक इतिहास')}</h3>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-slate-700">{t("Mother's Conditions", 'माता की बीमारियां')}:</p>
              <p>{patientData.intake.family_history.mother?.conditions?.join(', ') || t('None', 'कोई नहीं')}</p>
            </div>
            <div>
              <p className="font-medium text-slate-700">{t("Father's Conditions", 'पिता की बीमारियां')}:</p>
              <p>{patientData.intake.family_history.father?.conditions?.join(', ') || t('None', 'कोई नहीं')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Lifestyle from Intake */}
      {patientData.intake?.lifestyle && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 border-b pb-2">
            <Heart className="h-5 w-5 text-pink-500" />
            <h3 className="text-lg font-bold text-[#0D2340]">{t('Lifestyle', 'जीवनशैली')}</h3>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="bg-slate-50 p-3 rounded">
              <p className="text-xs text-slate-500">{t('Smoking', 'धूम्रपान')}</p>
              <p className="font-medium">{patientData.intake.lifestyle.smoking || '-'}</p>
            </div>
            <div className="bg-slate-50 p-3 rounded">
              <p className="text-xs text-slate-500">{t('Alcohol', 'शराब')}</p>
              <p className="font-medium">{patientData.intake.lifestyle.alcohol || '-'}</p>
            </div>
            <div className="bg-slate-50 p-3 rounded">
              <p className="text-xs text-slate-500">{t('Exercise', 'व्यायाम')}</p>
              <p className="font-medium">{patientData.intake.lifestyle.exercise || '-'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t-2 border-slate-200 pt-4 mt-8 text-center text-xs text-slate-500">
        <p className="flex items-center justify-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#0D2340]" />
          {t('This document is generated from EmergencyCard Health - Your Digital Health Passport', 'यह दस्तावेज़ EmergencyCard Health से जनरेट किया गया है')}
        </p>
        <p className="mt-1">{t('Blockchain secured and tamper-proof', 'ब्लॉकचेन सुरक्षित और छेड़छाड़-रोधी')}</p>
      </div>
    </div>
  );
});

PrintableHealthReport.displayName = 'PrintableHealthReport';

export default PrintableHealthReport;
